import {
  Flex,
  Box,
  Avatar,
  Icon,
  Grid,
  Center,
  Button,
  Link,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import { SanitizedBox } from '@/ui/SanitizedDom';
import { Time } from '@/ui/Time';
import { conversationConstants } from './constants';
import { getMessageStatusText } from './messageStatusText';
import { isInternalEmailConversation } from './internalEmailConversations';
import {
  conversationAnchorId,
  matchesHighlight,
} from './conversationHighlight';

/**
 * How long the jumped-to row stays tinted. Long enough to register after the
 * smooth scroll settles, short enough that it stops looking like a permanent
 * style on the message kind that happens to link here.
 */
const HIGHLIGHT_FLASH_MS = 2500;
// Frames the jumped-to row must hold still before it is scrolled to, and the most
// that will be waited for regardless (~half a second at 60fps, comfortably past
// Chakra's accordion transition).
const SETTLED_FRAMES = 2;
const MAX_SETTLE_FRAMES = 30;
import { ActivityMoreMenu } from '@/components/candidateActivities/kit';
import { InternalEmailScheduledActions } from './InternalEmailScheduledActions';
import { Badge } from '@kula-ai/components';
import { AttachmentPreview } from '@/components/outreach-attachment/AttachmentPreview';
import {
  ArrowSquareUpRight,
  Atom01,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  CornerUpLeft,
  Edit05,
  Mail02,
  MailInternal,
  PlusCircle,
  Send01,
  Trash01,
} from '@kula-ai/icons-core';
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  getHighlightProps,
  useTransientHighlight,
} from '@/components/shared/interview-plan/hooks/useTransientHighlight';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { KText } from '@/ui-library/components/text';
import { useKFlags } from '@/hooks/use-k-flags';
import moment from 'moment';
import { useDateFormat } from '@/hooks';
import { ATTACHMENT_API_SERVICE } from './api';
import { useShowToast } from '@/hooks';
import { useMutation } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
// Imports
import { useApplicationHelper as useApplicationHelperDefault } from '@/components/contacts/candidate-job-drawer/useApplicationHelper'; //('todo:cv2');
import { useApplicationHelper as useApplicationHelperV1 } from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
import { CandidateContext } from '@/components/candidate-job-slider-v1/context';
import { useFileValidation } from '@/hooks/useFileValidation';
import { useContext } from 'react';
import { atsAssessmentRecordType } from './utils';
import { useSendScheduledAssessmentNowMutation } from '@/store/query/assessment';
import { KButton } from '@/ui-library/components';
import { convertPlainTextToHtml } from '@/utils/textarea-parser';
import { TransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';
import { AISessionTranscript } from '@/components/ai-coordinator-sessions/components/AISessionTranscript';
import { AISessionAudioPlayer } from '@/components/ai-coordinator-sessions/components/AISessionAudioPlayer';

const INTERNAL_TAG_LABEL = 'Internal email';
// import { CoordinatorCallStatusBadge } from '@/components/ats/job-stage-actions/actions/interview/ScheduleWithAi/CoordinatorCallStatusBadge';

const hasTimeElapsed = (scheduled_at, timezone) => {
  return moment.tz(scheduled_at, timezone).isBefore(moment.tz(timezone));
};

const getTransferInfoForBadge = (transfer_info, atsApplicationId) => {
  if (!transfer_info) return null;
  const appTransferInfo =
    transfer_info?.applications?.[String(atsApplicationId)];
  const hasTransferredIn = !!appTransferInfo?.transferred_in;
  const hasTransferredOut = !!appTransferInfo?.transferred_out;
  const fromApplicationId =
    appTransferInfo?.transferred_in?.from_application_id;
  // TransferredBadge uses history: for transferred_in we pass [from_application_id]; for transferred_out we pass [current app id]
  const history = hasTransferredIn
    ? fromApplicationId
      ? [fromApplicationId]
      : []
    : hasTransferredOut
      ? [atsApplicationId]
      : [];
  return hasTransferredIn || hasTransferredOut
    ? {
        applications: transfer_info.applications || {},
        history,
      }
    : null;
};

const MessageContent = ({
  conversation,
  isFirstMessage = false,
  /**
   * This is the email another tab asked to be shown. Painted here rather than on
   * the thread around it: a thread can hold several emails, and highlighting the
   * whole row would point at all of them.
   */
  isHighlighted = false,
  message,
  onReply,
  onReplyInternalEmail = (_conversation?: unknown) => {},
  hasThread,
  onReplyCandidate = (_message?: unknown) => {},
  onSendNow = (_message?: unknown) => {},
  onDeleteEmail,
  onEditEmail,
  isFromApplication = false,
  isUpdating: _isUpdating = false,
  onRefetchScheduled,
  onEditInternalEmail,
  transferInfo = null,
}) => {
  const { feHtmlToText } = useKFlags();
  const { resourceUsage } = useContext(CandidateContext);
  const applicationHelper = useApplicationHelperDefault();
  const applicationHelperV2 = useApplicationHelperV1();
  const { applicationData } =
    resourceUsage === 'V2' ? applicationHelperV2 : applicationHelper;
  const { getIsAcceptedFileType } = useFileValidation();
  const { feSharedEmails } = useKFlags();
  const { timeFormat } = useDateFormat();
  const { scheduled_at, is_transferred = false } = conversation || {};
  const toast = useShowToast();
  const [attachmentStates, setAttachmentStates] = useState({});
  const applicationId = applicationData?.id;
  const ScheduledDate = moment.parseZone(scheduled_at).format('MMM D YYYY');
  const ScheduledTime = moment.parseZone(scheduled_at).format(timeFormat);

  const {
    mutateAsync: addEmailAttachmentsToFiles,
    isPending: isAddingAttachments,
  } = useMutation({
    mutationFn: ATTACHMENT_API_SERVICE.addEmailAttachmentsToFiles,
  });

  const [sendScheduledAssessmentNow, { isLoading: isSendingAssessment }] =
    useSendScheduledAssessmentNowMutation();

  const handleFileAttachment = async (attachment) => {
    try {
      // here conversation is the main message for the content and messages are the other messages in the same convo thread
      const response = await addEmailAttachmentsToFiles({
        application_id: conversation?.ats_application_id || applicationId,
        candidate_message_id: conversation?.id,
        attachment_id: attachment?.id,
      });
      if ((response as any)?.errors?.length) {
        toast({
          title: getErrorMessage((response as any)?.errors, 'attachments'),
          status: 'error',
        });
      } else {
        setAttachmentStates((prev) => ({
          ...prev,
          [attachment?.id]: 'success',
        }));
      }
    } catch (error) {
      console.error('Error adding attachment:', error);
    }
  };

  const scheduledAssessmentNow = async () => {
    const result = await sendScheduledAssessmentNow({
      assessmentId: conversation?.record?.id,
    });
    if ((result as any)?.error?.length) {
      toast({ title: 'Sending assessment failed', status: 'error' });
    } else {
      toast({ title: 'Assessment sent successfully' });
      if (onRefetchScheduled) {
        onRefetchScheduled();
      }
    }
  };

  const renderAttachmentAction = (attachment) => {
    // Get current state
    const attachmentStatus =
      attachmentStates[attachment?.id] ||
      (attachment?.is_added_to_files === true ? 'success' : 'idle');
    const isSuccess =
      attachmentStatus === 'success' || attachment?.is_added_to_files === true;

    // Check if file type is supported and under size limit (30MB)
    const isValidFileType = getIsAcceptedFileType(attachment);
    const isValidFileSize = attachment.byte_size < 30 * 1024 * 1024;
    const isFileValid = isValidFileType && isValidFileSize;

    if (isSuccess) {
      return (
        <Tooltip
          label="Added to files section"
          shouldWrapChildren
          hasArrow
          placement="top"
        >
          <CheckCircle
            size={18}
            color="var(--kula-colors-success-200)"
            className="kula-icon"
            style={{ marginTop: '6px' }}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip
        label={
          !isValidFileType
            ? 'File type is not supported'
            : !isValidFileSize
              ? 'File size is too large'
              : ''
        }
        shouldWrapChildren
        hasArrow
        placement="top"
      >
        <Flex
          gap="6px"
          alignItems="center"
          color="primary-500"
          cursor={!isFileValid ? 'not-allowed' : 'pointer'}
          onClick={() =>
            isFileValid &&
            !isAddingAttachments &&
            handleFileAttachment(attachment)
          }
          opacity={!isFileValid ? 0.5 : 1}
        >
          <PlusCircle
            size={18}
            color="var(--kula-colors-primary-500)"
            className="kula-icon"
          />
          <KText as="span" color="primary-500">
            Add to files
          </KText>
        </Flex>
      </Tooltip>
    );
  };

  const renderMoreMenu = () => {
    // Handle sending and deleting scheduled assessment for AtsAssessment record type
    if (conversation?.record?.type === atsAssessmentRecordType) {
      if (conversation?.status === 'scheduled') {
        return (
          <KButton
            variant="secondary"
            size="sm"
            onClick={scheduledAssessmentNow}
            isLoading={isSendingAssessment}
            data-test-id="conversation-assessment-send-now-button"
          >
            <Flex gap="5px" alignItems="center">
              <Icon
                as={Edit05 as any}
                height="14px"
                width="14px"
                color="ats-primary"
              />
              <KText size="sm-semi-bold" color="ats-primary">
                Send now
              </KText>
            </Flex>
          </KButton>
        );
      }
      return null;
    }
    // An internal email is an `AtsUserMessage`, and every handler this tab passes
    // down addresses candidate messages by id — so it brings its own.
    if (isInternalEmailConversation(conversation)) {
      return (
        <InternalEmailScheduledActions
          internalEmailId={conversation?.record?.id ?? conversation?.id}
          onEdit={
            onEditInternalEmail
              ? () => onEditInternalEmail(conversation)
              : undefined
          }
          onDone={onRefetchScheduled}
        />
      );
    }

    // The kit menu the Overview tab's scheduled rows already use, so one
    // scheduled email offers the same "More" affordance wherever it is read.
    // What stood here was a hand-rolled Chakra menu with its own trigger box and
    // its own item chrome — the drift `ActivityMoreMenu` exists to end.
    return (
      <ActivityMoreMenu
        data-test-id="conversation-email-more-menu-button"
        items={[
          {
            id: 'send-now',
            label: 'Send now',
            icon: Send01,
            onClick: () => onSendNow(message),
          },
          {
            id: 'edit',
            label: 'Edit email',
            icon: Edit05,
            onClick: () => onEditEmail(message),
          },
          {
            id: 'delete',
            label: 'Delete email',
            icon: Trash01,
            destructive: true,
            onClick: () => onDeleteEmail && onDeleteEmail(message),
          },
        ]}
      />
    );
  };

  return (
    <Box
      bg="secondary-25"
      borderRadius="14px"
      p="20px"
      ml="12px"
      flex="1"
      mb={hasThread ? '12px' : '24px'}
      // Anchored per message, not per thread: a jump addresses one email, and the
      // thread around it may hold several. An `id` rather than a test id, because
      // `getHighlightProps` stamps its own `data-test-id` on whatever it marks.
      id={conversationAnchorId(conversation?.id)}
      // The same "here it is" edge the interview plan draws on a freshly created
      // activity: a 3px inset outline, which takes no space, so nothing in the
      // thread shifts as it appears and expires.
      {...getHighlightProps(isHighlighted)}
    >
      <Flex justifyContent="space-between" gap="12px">
        <Flex direction="column" gap="8px">
          {isFirstMessage &&
            ((conversation?.ats_job && conversation?.ats_job?.id) ||
              (conversation?.outreach && conversation?.outreach?.id)) && (
              <Flex
                p="4px 8px"
                borderRadius="sm"
                bg="white"
                border="1px solid"
                borderColor="border-color"
                alignItems="center"
                gap="6px"
                key={conversation?.ats_job?.id || conversation?.outreach?.id}
                width="fit-content"
              >
                <KText size="sm-medium">
                  {conversation?.ats_job ? 'Job' : 'Flow'}:
                </KText>
                <Link
                  href={
                    conversation?.ats_job?.id
                      ? `/jobs/${conversation?.ats_job?.id}?tab=candidates&appId=${conversation?.ats_application_id}`
                      : `/flows/${conversation?.outreach?.id}/candidates`
                  }
                  isExternal
                  display="flex"
                  gap="4px"
                  alignItems="center"
                  _hover={{ textDecoration: 'none' }}
                >
                  <TruncatedTextWithTooltip
                    text={
                      conversation?.ats_job?.name ||
                      conversation?.outreach?.title
                    }
                    color="primary-600"
                    maxW="400px"
                    size="sm-medium"
                  />
                  <Icon as={ArrowSquareUpRight as any} color="primary-600" />
                </Link>
              </Flex>
            )}
          <Flex alignItems="center" gap="6px">
            {isFirstMessage && (
              <KText
                size="md-semi-bold"
                color="secondary-900"
                wordBreak="break-word"
              >
                {conversation?.subject}
              </KText>
            )}
            {/* The candidate never received this one — said beside the subject,
                where the reader is already looking (Figma 19366:8456). */}
            {isFirstMessage && isInternalEmailConversation(conversation) && (
              <Badge
                type="outline"
                color="primary"
                data-test-id="internal-email-tag"
              >
                {INTERNAL_TAG_LABEL}
              </Badge>
            )}
            {is_transferred && (
              <TransferredBadge
                transferInfo={transferInfo}
                sourceApplicationId={conversation?.ats_application_id}
              />
            )}
          </Flex>
        </Flex>
        {isFirstMessage && conversation?.repliable && (
          <Button
            variant="ats-secondary"
            cursor="pointer"
            minWidth="fit-content"
            onClick={() => onReply(message)}
          >
            <Flex gap="5px">
              <CornerUpLeft size={16} color="#4A4A4A" className="kula-icon" />
              <KText
                size="sm-semi-bold"
                data-test-id="conversation-email-reply-button"
              >
                Reply
              </KText>
            </Flex>
          </Button>
        )}
        {/* Its own button, not the one above: `repliable` gates the in-app reply,
            which writes to the candidate. This one answers the teammates instead,
            as another internal email. Only once it has actually gone out — a
            queued one is still editable, which is what its own row offers. */}
        {isFirstMessage &&
          isInternalEmailConversation(conversation) &&
          conversation?.sent_at && (
            <Button
              variant="ats-secondary"
              cursor="pointer"
              minWidth="fit-content"
              onClick={() => onReplyInternalEmail(conversation)}
            >
              <Flex gap="5px">
                <CornerUpLeft size={16} color="#4A4A4A" className="kula-icon" />
                <KText
                  size="sm-semi-bold"
                  data-test-id="conversation-internal-email-reply-button"
                >
                  Reply
                </KText>
              </Flex>
            </Button>
          )}
        {!conversation?.repliable &&
          conversation?.scheduled_at &&
          (conversation?.sent_at === null &&
          hasTimeElapsed(conversation?.scheduled_at, conversation?.timezone) ? (
            <Flex
              alignItems="center"
              border="1px solid"
              borderColor="border-color"
              p="2px 8px"
              borderRadius="xs"
            >
              <KText size="sm-medium" color="secondary-700">
                Sending email
              </KText>
            </Flex>
          ) : (
            <Flex alignItems="center" gap="12px" w="max-content">
              <Flex alignItems="center" gap="4px" w="max-content">
                <KText size="sm-medium">Scheduled:</KText>
                <KText>
                  {ScheduledDate}, {ScheduledTime}
                </KText>
              </Flex>
              {renderMoreMenu()}
            </Flex>
          ))}
      </Flex>
      <Flex justifyContent="space-between" mt="12px">
        <Flex justifyContent="start">
          <Avatar
            className="avatar-sm"
            name={conversation?.sender?.name}
            src={conversation?.sender?.avatar}
            borderRadius="4px"
            width="20px"
            height="20px"
          />
          <KText ml="4px">
            <KText as="span" color="secondary-900" size="sm-medium" mr="4px">
              {conversation?.sender?.name}
            </KText>
            <KText as="span" mr="4px">
              {getMessageStatusText(conversation)}
            </KText>
            <KText as="span" color="secondary-900" size="sm-medium" mr="4px">
              {conversation?.cc?.length > 0 && conversation?.bcc?.length > 0
                ? `${conversation?.recipient?.name},`
                : conversation?.cc?.length > 0
                  ? `${conversation?.recipient.name} and`
                  : conversation?.recipient?.name}
            </KText>
            {conversation?.sent_from?.name && (
              <>
                <KText as="span" size="sm-medium" mr="4px">
                  from
                </KText>
                <KText
                  as="span"
                  size="sm-medium"
                  mr="4px"
                  color="secondary-900"
                >
                  {feSharedEmails
                    ? conversation?.sent_from?.name
                    : 'Kula no-reply'}
                </KText>
                <KText as="span" size="sm-medium" mr="4px">
                  {feSharedEmails
                    ? `<${conversation?.sent_from?.email}>`
                    : '<no-reply@kula.ai>'}
                </KText>
              </>
            )}
            {conversation?.cc && conversation.cc.length > 0 && (
              <KText as="span" size="sm-medium" mr="4px">
                CC: {conversation?.cc?.map((user) => user.name).join(', ')}
              </KText>
            )}

            {conversation?.bcc && conversation.bcc.length > 0 && (
              <KText as="span" size="sm-medium" mr="4px">
                and BCC:{' '}
                {conversation?.bcc?.map((user) => user.name).join(', ')}
              </KText>
            )}
            {conversation?.ats_job?.id && (
              <>
                <KText as="span" mr="4px">
                  in job
                </KText>
                <KText as="span" color="secondary-900" size="sm-medium">
                  {conversation?.ats_job?.name}
                </KText>
              </>
            )}
          </KText>
          <KText as="span" ml="4px">
            <Time
              time={conversation?.sent_at}
              variant="muted"
              whiteSpace="nowrap"
              size="sm-regular"
            />
          </KText>
        </Flex>
        {isFirstMessage && conversation?.channel === 'kula_platform' && (
          <Button
            variant="ats-secondary"
            cursor="pointer"
            minWidth="fit-content"
            onClick={() => onReplyCandidate(message)}
          >
            <Flex gap="5px">
              <CornerUpLeft size={16} color="#4A4A4A" className="kula-icon" />
              <KText
                size="sm-semi-bold"
                data-test-id="conversation-email-reply-to-candidate-button"
              >
                Reply
              </KText>
            </Flex>
          </Button>
        )}
      </Flex>
      <Box mt="12px" className="editor-style">
        {conversation?.channel !== 'self_schedule' && (
          <SanitizedBox
            fontSize="14px"
            html={
              feHtmlToText
                ? convertPlainTextToHtml(conversation?.body || '')
                : conversation?.body
            }
            whiteSpace="pre-line"
            color="secondary-700"
          />
        )}
      </Box>
      {conversation?.attachments?.length > 0 && (
        <Grid gap="10px" p="20px" templateColumns="repeat(2, 1fr)">
          {conversation?.attachments?.map((attachment, idx) => {
            // Prepare attachment for display
            const displayFile = {
              ...attachment,
              name: attachment?.filename ?? attachment?.name,
              type: attachment?.content_type ?? attachment?.type,
              size: attachment?.byte_size ?? attachment?.size,
            };

            const attachmentAction = renderAttachmentAction(attachment);

            return (
              <AttachmentPreview
                key={idx}
                file={displayFile}
                bg="#fff"
                AfterContent={
                  (conversation?.ats_job?.id ||
                    (isFromApplication && applicationId)) &&
                  conversation?.category !== 'candidate_adhoc' &&
                  attachmentAction
                }
              />
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

const Conversation = ({
  conversation = {} as any,
  highlight = null,
  otherMessages = [],
  handleReplyClick = (_message?: unknown) => {},
  handleCandidateReply = (_message?: unknown) => {},
  handleReplyInternalEmail = (_conversation?: unknown) => {},
  handleSendNow = (_message?: unknown) => {},
  handleDeleteEmail = () => {},
  handleEditEmail = () => {},
  isFromApplication = false,
  isUpdating = false,
  onRefetchScheduled,
  onEditInternalEmail,
  transferInfo = null,
}: {
  conversation?: any;
  highlight?: any;
  otherMessages?: any[];
  handleReplyClick?: (_message?: unknown) => void;
  handleCandidateReply?: (_message?: unknown) => void;
  handleReplyInternalEmail?: (_conversation?: unknown) => void;
  handleSendNow?: (_message?: unknown) => void;
  handleDeleteEmail?: () => void;
  handleEditEmail?: () => void;
  isFromApplication?: boolean;
  isUpdating?: boolean;
  onRefetchScheduled?: () => void;
  onEditInternalEmail?: (_conversation: any) => void;
  transferInfo?: any;
}) => {
  const {
    icon: channelIcon = Mail02,
    bg = 'pastel-3-light',
    color = 'pastel-3-dark',
  } = conversationConstants[conversation?.channel];
  // The same mark the activity row gives an internal email, so one email reads as
  // the same thing wherever it is listed. Its channel is still email — that is what
  // decides the rest of the row's chrome — so only the glyph changes.
  const icon = isInternalEmailConversation(conversation)
    ? MailInternal
    : channelIcon;
  const [showMore, setShowMore] = useState(false);

  // Arrived here from another tab asking for one of these emails: bring it into
  // view and flash it once, so the reader can see which of the listed emails they
  // were sent to.
  //
  // A row is a THREAD — its opening message plus every reply — and the asked-for
  // email is often one of the replies, folded away behind "Show N more emails".
  // Matching only the opening message lit up the top of the thread whatever was
  // asked for, and left the actual email collapsed out of sight. So the whole
  // thread is searched, the thread opens if the match is inside it, and the flash
  // lands on that message.
  //
  // The flash is its own short-lived state rather than the highlight itself. The
  // highlight has to stand for as long as the tab is open — it is what forces the
  // accordion section holding this row open (`ApplicationConversations`) — so
  // painting straight off it left every jumped-to row highlighted permanently.
  const requestedMessage = useMemo(
    () =>
      [conversation, ...(otherMessages ?? [])].find((message) =>
        matchesHighlight(message, highlight)
      ) ?? null,
    [conversation, otherMessages, highlight]
  );
  const { highlight: flash, isHighlighted: isFlashing } =
    useTransientHighlight(HIGHLIGHT_FLASH_MS);

  // Unfolded during render rather than from an effect, so the thread never paints a
  // frame with the asked-for email still hidden — and only once per request, which
  // is what leaves "Hide thread" working afterwards. The highlight itself stands for
  // as long as the tab is open, so deriving the open state from it would hold the
  // thread open against the reader.
  const [openedFor, setOpenedFor] = useState<string | number | null>(null);
  const isReplyInThread = Boolean(
    requestedMessage && requestedMessage !== conversation
  );
  if (isReplyInThread && openedFor !== requestedMessage.id) {
    setOpenedFor(requestedMessage.id);
    setShowMore(true);
  }

  const rowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!requestedMessage) return;

    flash(requestedMessage.id);

    // The email itself, not the thread around it: a reply sits below the message
    // that opened the thread, and centring the thread leaves it off screen.
    const target = () => {
      const anchor = conversationAnchorId(requestedMessage.id);
      return (anchor && document.getElementById(anchor)) || rowRef.current;
    };

    // Once, and only when the row has stopped moving.
    //
    // It is still moving when this effect runs: the section holding it is an
    // accordion panel animating open (it was collapsed until the highlight forced
    // it), and a thread that just unfolded has grown by every reply in it. Scrolling
    // into a moving target lands where the row used to be — and correcting that with
    // a second scroll afterwards reads as the page jumping twice.
    //
    // So the row's position is sampled each frame until two agree, and the scroll
    // happens then: one smooth glide, however long the animation took.
    let frame = 0;
    let previousTop: number | null = null;
    let stillFrames = 0;
    let waited = 0;

    const settleThenScroll = () => {
      const element = target();
      if (!element) return;

      const { top } = element.getBoundingClientRect();
      stillFrames =
        previousTop !== null && Math.abs(top - previousTop) < 1
          ? stillFrames + 1
          : 0;
      previousTop = top;
      waited += 1;

      // The cap is the backstop for a layout that never settles — a spinner in the
      // thread, say. Scrolling a little early beats not scrolling at all.
      if (stillFrames >= SETTLED_FRAMES || waited >= MAX_SETTLE_FRAMES) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      frame = requestAnimationFrame(settleThenScroll);
    };

    frame = requestAnimationFrame(settleThenScroll);

    return () => cancelAnimationFrame(frame);
  }, [requestedMessage?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastMessage = otherMessages?.length
    ? otherMessages[otherMessages?.length - 1]
    : conversation;

  return (
    <Flex
      ref={rowRef}
      className="card"
      data-test-id="conversation-email-section"
    >
      <Flex flexDir="column" alignItems="center">
        <Center w="34px" h="34px" borderRadius="full" bg={bg}>
          <Icon as={icon} width="17px" height="17px" color={color} />
        </Center>
        <Box flex="1" borderColor="light.border" className="thread-line" />
      </Flex>
      <Flex flexDir="column" flex="1">
        <MessageContent
          conversation={conversation}
          isFirstMessage
          isHighlighted={isFlashing(conversation?.id)}
          onReply={handleReplyClick}
          onReplyCandidate={handleCandidateReply}
          onReplyInternalEmail={handleReplyInternalEmail}
          onSendNow={handleSendNow}
          onDeleteEmail={handleDeleteEmail}
          onEditEmail={handleEditEmail}
          message={lastMessage}
          hasThread={otherMessages?.length > 0}
          isFromApplication={isFromApplication}
          isUpdating={isUpdating}
          onRefetchScheduled={onRefetchScheduled}
          onEditInternalEmail={onEditInternalEmail}
          transferInfo={transferInfo}
        />
        {otherMessages?.length > 0 && (
          <>
            {showMore &&
              otherMessages?.map((item, index) => (
                <MessageContent
                  key={index}
                  message={item}
                  conversation={item}
                  isHighlighted={isFlashing(item?.id)}
                  onReply={handleReplyClick}
                  onSendNow={handleSendNow}
                  onDeleteEmail={handleDeleteEmail}
                  onEditEmail={handleEditEmail}
                  hasThread={otherMessages?.length > 0}
                  isFromApplication={isFromApplication}
                  isUpdating={isUpdating}
                  onRefetchScheduled={onRefetchScheduled}
                  onEditInternalEmail={onEditInternalEmail}
                  transferInfo={transferInfo}
                />
              ))}
            <Center
              bg="ats-bg-primary"
              ml="12px"
              cursor="pointer"
              h="60px"
              mb="24px"
              borderRadius="sm"
              onClick={() => setShowMore((prev) => !prev)}
              position="relative"
              gap="12px"
            >
              <KText size="sm-semi-bold" color="ats-primary">
                {showMore
                  ? 'Hide thread'
                  : `Show ${otherMessages?.length} more email${
                      otherMessages?.length > 1 ? 's' : ''
                    }`}
              </KText>
              <Icon
                as={showMore ? ChevronUp : ChevronDown}
                color="ats-primary"
              />
              {showMore && lastMessage?.repliable && (
                <Button
                  position="absolute"
                  right="20px"
                  variant="ats-secondary"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReplyClick(lastMessage);
                  }}
                >
                  <Flex gap="5px">
                    <CornerUpLeft
                      size={16}
                      color="#4A4A4A"
                      className="kula-icon"
                    />
                    <KText
                      size="sm-semi-bold"
                      data-test-id="conversation-email-show-more-reply-button"
                    >
                      Reply
                    </KText>
                  </Flex>
                </Button>
              )}
            </Center>
          </>
        )}
      </Flex>
    </Flex>
  );
};

const AISessionConversation = ({ session, candidateName }) => {
  const hasTranscript = session?.transcript && session.transcript.length > 0;
  const hasRecording =
    !!session?.recording_url && session?.recording_duration_seconds !== null;

  // Map conversation session to activityData shape expected by CoordinatorCallStatusBadge
  // const activityData =
  //   session !== null
  //     ? { ...session, ai_call_status: session?.status ?? 'unknown' }
  //     : null;

  return (
    <Flex className="card" data-test-id="conversation-ai-session-section">
      <Flex flexDir="column" alignItems="center">
        <Center w="34px" h="34px" borderRadius="full" bg="secondary-950">
          <Atom01
            size={17}
            color="var(--kula-colors-white)"
            className="kula-icon"
          />
        </Center>
        <Box flex="1" borderColor="light.border" className="thread-line" />
      </Flex>
      <Flex flexDir="column" flex="1">
        <Box
          bg="primary-25"
          borderRadius="14px"
          p="20px"
          ml="12px"
          flex="1"
          mb="24px"
        >
          <Flex
            alignItems="baseline"
            mb="12px"
            gap="12px"
            justifyContent="space-between"
          >
            {/* <SessionStatusBadge status={session?.status || 'unknown'} /> */}
            <KText
              size="md-semi-bold"
              fontWeight="600"
              color="secondary-900"
              w="80%"
            >
              {session?.direction === 'outbound'
                ? 'Kula Coordinator has called the candidate to schedule a time for screening interview'
                : 'Candidate has scheduled a time for screening interview'}
            </KText>
            <Flex
              shrink={0}
              gap="8px"
              alignItems="center"
              justifyContent="flex-end"
              w="20%"
            >
              {/* <CoordinatorCallStatusBadge
                activityData={activityData}
                isActiveSession={false}
              /> */}
              <Time
                time={session?.created_at}
                size="xs-regular"
                color="secondary-500"
              />
            </Flex>
          </Flex>

          {hasTranscript && (
            <Box mb={hasRecording ? '16px' : 0}>
              <AISessionTranscript
                transcript={session.transcript}
                candidateName={candidateName || session.candidateName}
                candidateAvatarUrl={session.candidateAvatarUrl}
              />
            </Box>
          )}

          {hasRecording && (
            <AISessionAudioPlayer
              recordingUrl={session.recording_url}
              durationSeconds={session.recording_duration_seconds || 0}
            />
          )}

          {!hasTranscript && !hasRecording && (
            <Flex justifyContent="center" py="8px">
              <KText size="sm-regular" color="secondary-500">
                No transcript or recording available for this session.
              </KText>
            </Flex>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

export const ConversationContent = ({
  conversations = [],
  handleReplyClick = (_message?: any) => {},
  handleCandidateReply = (_message?: any) => {},
  handleReplyInternalEmail = (_conversation?: any) => {},
  handleSendNow = (_message?: any) => {},
  handleDeleteEmail = (_message?: any) => {},
  handleEditEmail = (_message?: any) => {},
  isFromApplication = false,
  isUpdating = false,
  onRefetchScheduled,
  onEditInternalEmail,
  highlight = null,
  transferInfo = null,
  transfer_info = null,
  candidateName = null,
  isAiSessionsLoading = false,
}: any) => {
  const isUnifiedFormat =
    conversations?.length > 0 &&
    conversations[0] &&
    typeof conversations[0] === 'object' &&
    'type' in conversations[0] &&
    'data' in conversations[0];

  if (isAiSessionsLoading && (!conversations || conversations.length === 0)) {
    return (
      <Center
        minH="160px"
        py="24px"
        mt="16px"
        data-test-id="conversation-content-ai-sessions-loading"
      >
        <Spinner color="primary-800" size="lg" thickness="4px" />
      </Center>
    );
  }

  return (
    <Box mt="16px">
      {isAiSessionsLoading && conversations?.length > 0 && (
        <Center
          py="12px"
          mb="8px"
          data-test-id="conversation-content-ai-sessions-inline-loading"
        >
          <Spinner color="primary-800" size="md" thickness="3px" />
        </Center>
      )}
      {conversations?.map((item, idx) => {
        if (isUnifiedFormat) {
          const { type, data } = item;
          const key =
            type === 'ai_session' ? `session-${data?.id}` : `email-${idx}`;
          if (type === 'ai_session') {
            return (
              <AISessionConversation
                key={key}
                session={data}
                candidateName={candidateName}
              />
            );
          }
          const firstMessage = data[data?.length - 1];
          const otherMessages = data?.slice(0, -1)?.reverse() || [];
          return (
            <Conversation
              key={key}
              conversation={firstMessage}
              otherMessages={otherMessages}
              handleReplyClick={handleReplyClick}
              handleCandidateReply={handleCandidateReply}
              handleReplyInternalEmail={handleReplyInternalEmail}
              handleSendNow={handleSendNow}
              handleDeleteEmail={handleDeleteEmail}
              handleEditEmail={handleEditEmail}
              isFromApplication={isFromApplication}
              isUpdating={isUpdating}
              onRefetchScheduled={onRefetchScheduled}
              onEditInternalEmail={onEditInternalEmail}
              highlight={highlight}
              transferInfo={transferInfo}
            />
          );
        }
        const conversation = item;
        const firstMessage = conversation[conversation?.length - 1];
        const otherMessages = conversation?.slice(0, -1)?.reverse();
        const threadTransferInfo = transfer_info
          ? getTransferInfoForBadge(
              transfer_info,
              firstMessage?.ats_application_id
            )
          : transferInfo;
        return (
          <Conversation
            key={idx}
            conversation={firstMessage}
            otherMessages={otherMessages}
            handleReplyClick={handleReplyClick}
            handleCandidateReply={handleCandidateReply}
            handleReplyInternalEmail={handleReplyInternalEmail}
            handleSendNow={handleSendNow}
            handleDeleteEmail={handleDeleteEmail}
            handleEditEmail={handleEditEmail}
            isFromApplication={isFromApplication}
            isUpdating={isUpdating}
            onRefetchScheduled={onRefetchScheduled}
            onEditInternalEmail={onEditInternalEmail}
            highlight={highlight}
            transferInfo={threadTransferInfo}
          />
        );
      })}
    </Box>
  );
};
